import {
  IMultipleRowActions,
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IPatientEncounterExecuteRequest } from "../../../../models/patient-encounter-execute-request";
import { reprocessPatientEncounterExecuteRequests } from "../../../../services/patientEncounterExecuteRequest.service";
import MainApi from "../../../../services/mainApi.service";
import PatientEncounterExecuteRequestDetailsModal from "./PatientEncounterExecuteRequestDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const PATIENT_ENCOUNTER_EXECUTE_REQUEST_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/PatientEncounterExecuteRequest/Search";

const patientEncounterExecuteRequestService =
  new MainApi<IPatientEncounterExecuteRequest>(
    PATIENT_ENCOUNTER_EXECUTE_REQUEST_ROUTE,
  );

const MANUAL_REVIEW_STATUS = "MANUAL_REVIEW";

const translationPath = "page.4uBus.logs.patientEncounterExecuteRequest.";

const PatientEncounterExecuteRequest = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IPatientEncounterExecuteRequest | null>(null);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    CreatedStart: null,
    CreatedEnd: null,
    RequestIdentifier: "",
    AuthorizationIntegrationId: "",
    ProcedureIntegrationId: "",
    IndividualBeneficiaryId: "",
    IndividualBeneficiaryName: "",
    PatientEncounterNumber: "",
    PrescriptionNumber: "",
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { sendRequest: reprocessRequest } = useFetch(
    reprocessPatientEncounterExecuteRequests,
  );

  const statusConfig = useMemo(
    () =>
      ({
        PENDING: { color: "info" as const, key: "pending" },
        PROCESSING: { color: "warning" as const, key: "processing" },
        SUCCESS: { color: "success" as const, key: "success" },
        MANUAL_REVIEW: { color: "error" as const, key: "manual_review" },
      }) as Record<string, { color: "info" | "warning" | "success" | "error"; key: string }>,
    [],
  );

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";

      return format(date, "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  const columns: GridColDef<IPatientEncounterExecuteRequest>[] = [
    {
      field: "requestIdentifier",
      headerName: t(translationPath + "request_identifier") || "Identificador da Requisição",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "authorizationIntegrationId",
      headerName: t(translationPath + "authorization_integration_id") || "Autorização",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "procedureIntegrationId",
      headerName: t(translationPath + "procedure_integration_id") || "Procedimento",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "individualBeneficiaryName",
      headerName: t(translationPath + "individual_beneficiary_name") || "Beneficiário",
      flex: 1.2,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "statusSend",
      headerName: t(translationPath + "status") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";
        const config = statusConfig[params.value] ?? {
          color: "info" as const,
          key: "pending",
        };

        return (
          <Chip
            label={t(translationPath + `status_values.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.6,
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: "patientEncounterNumber",
      headerName: t(translationPath + "patient_encounter_number") || "Nº Atendimento",
      flex: 0.8,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "prescriptionNumber",
      headerName: t(translationPath + "prescription_number") || "Nº Prescrição",
      flex: 0.8,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      onRefreshHandler();
    },
    [onRefreshHandler],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleReprocess = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      const { success } = await reprocessRequest({ ids });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(translationPath + "reprocessedSuccess"),
          type: "success",
        });
      }
    },
    [onRefreshHandler, reprocessRequest, setMessage, t],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids) => handleReprocess(ids.map((id) => String(id))),
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "reprocess_selected") || "Reprocessar selecionados",
      },
    ],
    [handleReprocess, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <PatientEncounterExecuteRequestDetailsModal
            patientEncounterExecuteRequest={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>

      <Table<IPatientEncounterExecuteRequest>
        title={t("components.menu.4ubus.logs.patientEncounterExecuteRequest")}
        columns={columns}
        service={patientEncounterExecuteRequestService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Pesquisar..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => row.id}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          params.row.statusSend === MANUAL_REVIEW_STATUS
            ? {
                action: () => handleReprocess([params.row.id]),
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "reprocess") || "Reprocessar",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            CreatedStart: filterParams.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default PatientEncounterExecuteRequest;
